import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SessionStorageService } from '../../services/session-storage.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { Platform } from '@angular/cdk/platform';
import { EnvironmentService } from '../../services/environment.service';
import { saveAs } from '@progress/kendo-file-saver';
import cloneDeep from 'lodash-es/cloneDeep';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgxLoadingModule } from 'ngx-loading';

@Component({
  selector: 'app-ag-print',
  templateUrl: './ag-print.component.html',
  styleUrls: ['./ag-print.component.scss'],
  imports: [
    MatInputModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    NgxLoadingModule,
    FormsModule,
  ],
})
export class AgPrintComponent implements OnInit {
  private rbs = inject(RightBridgeApiService);
  private params = inject(ActivatedRoute);
  private ss = inject(SessionStorageService);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);

  @Input() profile;
  @Input() silentPrint;
  @Input() customDefinition;
  @Output() closeDialog = new EventEmitter();

  printForm: FormGroup;
  printParams;
  appAccess = [];
  enablePrint = {
    AnnuityWizard: true,
    ProductProfiler: true,
    InvestmentWizard: true,
    LifeWizard: true,
  };
  loading = false;
  awProductsSelected = false;
  rights: string[];
  firm;
  environment;
  baseUrl;
  generating = false;
  filename;

  ngOnInit(): void {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.loading = true;
    this.appAccess = this.ss.get('globals')?.Modules;
    this.rights = this.ss.get('rights');
    this.firm = this.ss.get('firm');

    this.getPrintData();
  }

  getPrintData() {
    this.rbs
      .getCustomPrintParameters(this.profile, this.customDefinition?.GroupName)
      .subscribe(res => {
        this.printParams = res;
        if (
          Object.keys(this.printParams.AWSelectedProduct).length > 0 &&
          this.printParams.AWSelectedProduct.CarrierName.length > 0
        ) {
          this.awProductsSelected = true;
        }

        if (
          !this.printParams.AWProfilingResults.AWRan ||
          this.printParams.AWProfilingResults.AWRan !== 'Y' ||
          !this.awProductsSelected
        ) {
          this.enablePrint.AnnuityWizard = false;
        }

        if (
          !this.printParams.IWProfilingResults.IWRan ||
          this.printParams.IWProfilingResults.IWRan !== 'Y'
        ) {
          this.enablePrint.InvestmentWizard = false;
        }

        if (
          !this.printParams.LIProfilingResults.LIRan ||
          this.printParams.LIProfilingResults.LIRan !== 'Y'
        ) {
          this.enablePrint.LifeWizard = false;
        }

        this.loading = false;
      });
  }

  updateApps(ev, app) {
    this.enablePrint[app] = !this.enablePrint[app];
  }

  cancel() {
    this.loading = false;
    this.closeDialog.emit({
      result: 'cancel',
    });
  }

  generateReport() {
    this.loading = true;

    const payload = {
      ReportType: this.customDefinition.ReportName,
      ProfileID: this.profile,
      ...cloneDeep(this.enablePrint),
    };

    if (this.silentPrint) {
      this.closeDialog.emit(payload);
    } else {
      this.generating = true;

      this.rbs
        .getCustomReport(payload, this.customDefinition.Solution)
        .subscribe({
          next: data => {
            const date = new Date();
            const filename = this.filename
              ? this.filename
              : 'RightBridge-Report-' +
                (date.getMonth() + 1) +
                date.getDate() +
                date.getFullYear() +
                '.pdf';

            const iosSafari =
              this.pltfrm.IOS ||
              (navigator?.['userAgentData']?.platform === 'macOS' &&
                navigator.maxTouchPoints > 1);

            let fileData;
            const reader = new FileReader();
            reader.readAsDataURL(data);
            reader.onloadend = () => {
              fileData = reader.result;
              saveAs(fileData, filename, {
                proxyURL: `${this.baseUrl}/util/proxy`,
                proxyTarget: '_self',
                forceProxy: iosSafari,
              });
              this.closeDialog.emit('report generated');
              this.generating = false;
              this.loading = false;
            };
          },
          error: catchError(err => {
            this.loading = false;
            return throwError(() => err.error || 'Server error');
          }),
        });
    }
  }
}
